<template>
  <div class="labcheckup-guide">

    <!-- Hero -->
    <div class="hero-section">
      <div class="hero-content">
        <div class="app-badge">Десктоп-приложение для Windows</div>
        <h1 class="hero-title">ЛабЧекап</h1>
        <p class="hero-subtitle">
          Быстрый ML-прогноз лабораторных показателей прямо на вашем компьютере.
          Без браузера, без лишних шагов — запустили, ввели данные, получили результат.
        </p>
        <a :href="downloadUrl" class="btn-download">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
            <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5z"/>
            <path d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708l3 3z"/>
          </svg>
          Скачать ЛабЧекап
        </a>
        <p class="version-note">
          {{ versionLabel }} · Windows 10 / 11 · ~8 МБ
        </p>
      </div>
    </div>

    <!-- Main content -->
    <div class="guide-body">

      <!-- What is it -->
      <section class="guide-section">
        <h2>Что такое ЛабЧекап?</h2>
        <p>
          ЛабЧекап — настольная программа для Windows, которая позволяет получать
          ML-прогнозы по лабораторным показателям пациента. Она использует те же
          обученные модели, что и веб-интерфейс <router-link to="/labcheckup-online">ЛабЧекап</router-link>,
          но работает в нативном окне и не требует браузера. Приложение обращается
          к защищённому API и возвращает прогноз за считанные секунды.
        </p>
        <div class="features-grid">
          <div class="feature-card">
            <span class="feature-icon">⚡</span>
            <strong>Быстро</strong>
            <span>Прогноз за 1–2 секунды</span>
          </div>
          <div class="feature-card">
            <span class="feature-icon">🔒</span>
            <strong>Безопасно</strong>
            <span>Доступ только по API-ключу</span>
          </div>
          <div class="feature-card">
            <span class="feature-icon">🧪</span>
            <strong>Несколько моделей</strong>
            <span>Липиды, HbA1c, ферритин и другие</span>
          </div>
          <div class="feature-card">
            <span class="feature-icon">💻</span>
            <strong>Нативное приложение</strong>
            <span>Работает без браузера</span>
          </div>
        </div>
      </section>

      <!-- Installation -->
      <section class="guide-section">
        <h2>Установка</h2>
        <ol class="steps-list">
          <li>
            <strong>Скачайте установщик</strong> — нажмите кнопку «Скачать ЛабЧекап» выше.
          </li>
          <li>
            <strong>Запустите</strong> <code>LabCheckUpClient-installer.exe</code>.
            При первом запуске Windows может показать предупреждение SmartScreen —
            нажмите «Подробнее» → «Всё равно запустить».
          </li>
          <li>
            <strong>WebView2</strong> устанавливается автоматически, если он ещё
            не присутствует в системе (встроен в Windows 11 и обновлённую Windows 10).
          </li>
          <li>
            После установки приложение появится в меню «Пуск» и в разделе
            «Приложения и возможности».
          </li>
        </ol>
      </section>

      <!-- Step 1: Login -->
      <section class="guide-section">
        <h2>Шаг 1 — Вход в приложение</h2>
        <div class="step-content">
          <div class="step-text">
            <p>
              При первом запуске отображается экран входа. Введите ваш <strong>API-ключ</strong>
              и нажмите «Войти». Ключ выдаётся при подключении к сервису LabHub ЛабЧекап.
            </p>
            <div class="info-box">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                <path d="M8 16A8 8 0 1 0 8 0a8 8 0 0 0 0 16zm.93-9.412-1 4.705c-.07.34.029.533.304.533.194 0 .487-.07.686-.246l-.088.416c-.287.346-.92.598-1.465.598-.703 0-1.002-.422-.808-1.319l.738-3.468c.064-.293.006-.399-.287-.47l-.451-.081.082-.381 2.29-.287zM8 5.5a1 1 0 1 1 0-2 1 1 0 0 1 0 2z"/>
              </svg>
              Ключ хранится только в памяти сеанса и не сохраняется на диск.
            </div>
          </div>
          <div class="screenshot-wrap">
            <img src="/images/labcheckup-screen1.png" alt="Экран входа ЛабЧекап" class="screenshot" />
            <p class="screenshot-caption">Экран входа — введите API-ключ и нажмите «Войти»</p>
          </div>
        </div>
      </section>

      <!-- Step 2: Working -->
      <section class="guide-section">
        <h2>Шаг 2 — Получение прогноза</h2>
        <div class="step-content">
          <div class="step-text">
            <p>После успешного входа откроется рабочий экран. Последовательность действий:</p>
            <ol class="steps-list">
              <li>Укажите <strong>возраст</strong> пациента (лет).</li>
              <li>Выберите <strong>пол</strong> из выпадающего списка.</li>
              <li>
                Выберите <strong>модель</strong> прогноза — список загружается
                автоматически. Каждая модель соответствует определённому
                лабораторному показателю (например, «ДиаСкрининг» прогнозирует HbA1c).
              </li>
              <li>
                Заполните <strong>параметры анализов</strong> — поля появляются
                автоматически в зависимости от выбранной модели. Значения вводятся
                в указанных единицах измерения (принимается как точка, так и запятая).
              </li>
              <li>
                Нажмите <strong>«Рассчитать прогноз»</strong>. Результат отобразится
                внизу экрана вместе с остатком доступных запросов.
              </li>
            </ol>
          </div>
          <div class="screenshot-wrap">
            <img src="/images/labcheckup-screen2.png" alt="Рабочий экран ЛабЧекап" class="screenshot" />
            <p class="screenshot-caption">Рабочий экран — параметры анализов и результат прогноза</p>
          </div>
        </div>
      </section>

      <!-- FAQ -->
      <section class="guide-section">
        <h2>Частые вопросы</h2>
        <div class="faq-list">
          <details class="faq-item">
            <summary>Приложение не запускается — что делать?</summary>
            <p>
              Убедитесь, что используете Windows 10 (версия 1803+) или Windows 11.
              Если система просит установить WebView2 Runtime, подтвердите установку —
              это штатная зависимость приложения.
            </p>
          </details>
          <details class="faq-item">
            <summary>Где получить API-ключ?</summary>
            <p>
              Ключ выдаётся при подключении к платформе LabHub ЛабЧекап.
              Свяжитесь с командой LabHub через контакты на сайте.
            </p>
          </details>
          <details class="faq-item">
            <summary>Что означает «Осталось запросов»?</summary>
            <p>
              Каждый API-ключ имеет лимит запросов в период (например, 1000/месяц).
              Счётчик отображается после каждого успешного прогноза. При исчерпании
              лимита обратитесь к администратору для продления.
            </p>
          </details>
          <details class="faq-item">
            <summary>Как обновить приложение?</summary>
            <p>
              Скачайте новый установщик по кнопке выше и запустите его — он заменит
              предыдущую версию. Установщик обновляет приложение без удаления настроек.
            </p>
          </details>
        </div>
      </section>

    </div>
  </div>
</template>

<script>
const S3_BASE = 'https://s3.twcstorage.ru/ae5d4e32-f83584c7-33bd-4aeb-bdf2-c23bee43bcdd/labcheckup_client_windows';

export default {
  name: 'LabCheckupGuide',
  data() {
    return {
      downloadUrl: `${S3_BASE}/LabCheckUpClient-latest-installer.exe`,
      versionLabel: 'v1.1.0',
    };
  },
  async created() {
    try {
      const res = await fetch(`${S3_BASE}/version.json`, { cache: 'no-store' });
      if (res.ok) {
        const info = await res.json();
        if (info.version) this.versionLabel = `${info.version}`;
        if (info.url)     this.downloadUrl  = info.url;
      }
    } catch {
      // S3 недоступен или CORS — оставляем дефолтные значения
    }
  },
};
</script>

<style scoped>
.labcheckup-guide {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

/* Hero */
.hero-section {
  background: #e3eeed;
  color: #fff;
  padding: 60px 24px 56px;
  text-align: center;
}
.hero-content {
  max-width: 640px;
  margin: 0 auto;
}
.app-badge {
  display: inline-block;
  background: rgba(255,255,255,0.15);
  border: 1px solid rgba(255,255,255,0.3);
  border-radius: 100px;
  padding: 4px 14px;
  font-size: 13px;
  letter-spacing: 0.3px;
  margin-bottom: 16px;
}
.hero-title {
  font-size: 48px;
  font-weight: 800;
  margin: 0 0 12px;
  letter-spacing: -1px;
}
.hero-subtitle {
  font-size: 17px;
  line-height: 1.6;
  opacity: 0.88;
  margin: 0 0 28px;
}
.btn-download {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: #3fe7de;
  color: #1e293b;
  font-weight: 700;
  font-size: 16px;
  padding: 13px 28px;
  border-radius: 100px;
  text-decoration: none;
  transition: background 0.2s, transform 0.1s;
}
.btn-download:hover {
  background: #2dd4cb;
  transform: translateY(-1px);
  color: #1e293b;
}
.version-note {
  margin-top: 12px;
  font-size: 13px;
  opacity: 0.65;
}

/* Guide body */
.guide-body {
  max-width: 860px;
  margin: 0 auto;
  padding: 0 24px 60px;
}

.guide-section {
  padding-top: 48px;
}
.guide-section h2 {
  font-size: 24px;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 16px;
  padding-bottom: 10px;
  border-bottom: 2px solid #e2e8f0;
}
.guide-section p {
  font-size: 15px;
  line-height: 1.7;
  color: #374151;
  margin-bottom: 12px;
}
.guide-section a {
  color: #2563eb;
  text-decoration: none;
}
.guide-section a:hover {
  text-decoration: underline;
}

/* Features */
.features-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 14px;
  margin-top: 20px;
}
.feature-card {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 18px 16px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.feature-icon { font-size: 24px; }
.feature-card strong { font-size: 14px; color: #1e293b; }
.feature-card span   { font-size: 13px; color: #64748b; }

/* Steps */
.steps-list {
  padding-left: 20px;
  font-size: 15px;
  color: #374151;
  line-height: 1.8;
}
.steps-list li { margin-bottom: 8px; }
.steps-list code {
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  padding: 1px 6px;
  font-size: 13px;
}

/* Step content with screenshot */
.step-content {
  display: flex;
  flex-direction: column;
  gap: 24px;
}
@media (min-width: 700px) {
  .step-content {
    flex-direction: row;
    align-items: flex-start;
    gap: 32px;
  }
  .step-text { flex: 1; }
  .screenshot-wrap { flex: 0 0 340px; }
}
.screenshot {
  width: 100%;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.08);
}
.screenshot-caption {
  font-size: 13px;
  color: #64748b;
  text-align: center;
  margin-top: 8px;
}

/* Info box */
.info-box {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 8px;
  padding: 12px 14px;
  font-size: 13px;
  color: #1e40af;
  margin-top: 14px;
  line-height: 1.5;
}
.info-box svg { flex-shrink: 0; margin-top: 1px; }

/* FAQ */
.faq-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.faq-item {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
}
.faq-item summary {
  font-size: 15px;
  font-weight: 600;
  color: #1e293b;
  padding: 14px 16px;
  cursor: pointer;
  list-style: none;
  display: flex;
  justify-content: space-between;
  align-items: center;
  user-select: none;
}
.faq-item summary::-webkit-details-marker { display: none; }
.faq-item summary::after {
  content: '+';
  font-size: 20px;
  font-weight: 300;
  color: #64748b;
}
.faq-item[open] summary::after { content: '−'; }
.faq-item p {
  font-size: 14px;
  color: #374151;
  margin: 0;
  padding: 0 16px 14px;
  line-height: 1.6;
}
</style>
